import { Eye, EyeOff, Undo2, Redo2, Trash2 } from "lucide-react";
/**
 * Canvas-rail controls — Canvas On/Off + Undo + Redo +
 * Delete-selected (M7.5 C1 / C2 / C3). Ports the legacy
 * `coach-draw-actions` row (`js/coaching/review.js:923-936`).
 *
 * Used by both `<TacticalBoardEditor>` and `<TelestratorCanvas>`
 * with the same shape — each editor owns the state (visibility,
 * undo stack, selection) and wires it through these props.
 */

export type CanvasRailControlsProps = {
  /** Whether the drawing layer is visible (Canvas On). */
  visible: boolean;
  compact?: boolean;
  /** Toggle the visible flag. */
  onToggleVisible: () => void;
  /** Stack depth — drives the Undo button's `disabled` state. */
  undoDepth: number;
  /** Restore the last undo snapshot. */
  onUndo: () => void;
  /** Redo-stack depth — drives the Redo button's `disabled` state. */
  redoDepth: number;
  /** Restore the last redo snapshot. */
  onRedo: () => void;
  /** True iff a shape/token is currently selected — drives
   * Delete-selected's `disabled` state. */
  hasSelection: boolean;
  /** Remove the selected shape/token. */
  onDeleteSelected: () => void;
  /** Optional testid prefix — defaults to `"canvas-rail"`. The
   * buttons get `${prefix}-toggle-visible` / `${prefix}-undo` /
   * `${prefix}-redo` / `${prefix}-delete-selected`. */
  testidPrefix?: string;
};

export function CanvasRailControls(props: CanvasRailControlsProps) {
  const prefix = props.testidPrefix ?? "canvas-rail";
  return (
    <div
      className="flex flex-wrap gap-1.5"
      role="group"
      aria-label="Canvas controls"
      data-testid={prefix}
    >
      <button
        type="button"
        onClick={props.onToggleVisible}
        aria-pressed={props.visible}
        data-testid={`${prefix}-toggle-visible`}
        className={[
          "rounded border px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors",
          props.visible
            ? "border-accent bg-accent/15 text-ink"
            : "border-rule bg-surface text-ink-muted hover:text-ink",
        ].join(" ")}
      >
        {props.compact ? (
          <>
            {props.visible ? (
              <Eye size={18} aria-hidden />
            ) : (
              <EyeOff size={18} aria-hidden />
            )}
            <span className="canvas-compact-label">
              Canvas {props.visible ? "On" : "Off"}
            </span>
          </>
        ) : (
          <>Canvas {props.visible ? "On" : "Off"}</>
        )}
      </button>
      <button
        type="button"
        onClick={props.onUndo}
        disabled={props.undoDepth === 0}
        data-testid={`${prefix}-undo`}
        title={`Undo (${props.undoDepth} step${props.undoDepth === 1 ? "" : "s"})`}
        className="rounded border border-rule bg-surface text-ink-muted hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors"
      >
        {props.compact ? (
          <>
            <Undo2 size={18} aria-hidden />
            <span className="canvas-compact-label">Undo</span>
          </>
        ) : (
          "Undo"
        )}
      </button>
      <button
        type="button"
        onClick={props.onRedo}
        disabled={props.redoDepth === 0}
        data-testid={`${prefix}-redo`}
        title={`Redo (${props.redoDepth} step${props.redoDepth === 1 ? "" : "s"})`}
        className="rounded border border-rule bg-surface text-ink-muted hover:text-ink disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors"
      >
        {props.compact ? (
          <>
            <Redo2 size={18} aria-hidden />
            <span className="canvas-compact-label">Redo</span>
          </>
        ) : (
          "Redo"
        )}
      </button>
      <button
        type="button"
        onClick={props.onDeleteSelected}
        disabled={!props.hasSelection}
        data-testid={`${prefix}-delete-selected`}
        className="rounded border border-destructive/40 text-destructive disabled:opacity-40 disabled:cursor-not-allowed hover:bg-destructive/10 px-2 py-1 text-xs font-display uppercase tracking-wide transition-colors"
      >
        {props.compact ? (
          <>
            <Trash2 size={18} aria-hidden />
            <span className="canvas-compact-label">Delete</span>
          </>
        ) : (
          "Delete"
        )}
      </button>
    </div>
  );
}
